import { useParams, Navigate, Link as RouterLink, useSearchParams } from 'react-router';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { Box, Breadcrumbs, Button, ButtonGroup, Container, Link, Paper, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavBar from '@/components/nav-bar/nav-bar';
import LegalHeader from './legal-header';
import LegalIndexEn from './content/legal-index.en';
import LegalIndexDe from './content/legal-index.de';
import { findLegalDocument, legalDocuments } from './legal-documents';
import { supportedLocales, type SupportedLocales } from '@/i18n/i18n';

function getEffectiveLanguage(langParam: string | null): SupportedLocales {
  if (langParam && (supportedLocales as readonly string[]).includes(langParam)) {
    return langParam as SupportedLocales;
  }
  const browserLang = navigator.language?.substring(0, 2);
  if (browserLang && (supportedLocales as readonly string[]).includes(browserLang)) {
    return browserLang as SupportedLocales;
  }
  return 'en';
}

const languageLabels: Record<SupportedLocales, string> = {
  en: 'EN',
  de: 'DE',
};

const indexComponents: Record<SupportedLocales, typeof LegalIndexEn> = {
  en: LegalIndexEn,
  de: LegalIndexDe,
};

export default function LegalPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = getEffectiveLanguage(searchParams.get('lang'));
  const document = slug ? findLegalDocument(slug) : undefined;

  if (slug && !document) {
    return <Navigate to="/legal" replace />;
  }

  const handleLanguageChange = (newLang: SupportedLocales) => {
    setSearchParams({ lang: newLang }, { replace: true });
  };

  const IndexComponent = indexComponents[lang];
  const DocumentComponent = document?.component[lang];

  return (
    <>
      <SignedIn>
        <NavBar />
      </SignedIn>
      <SignedOut>
        <LegalHeader />
      </SignedOut>

      <Box sx={{ pt: { xs: '50px', md: '64px' } }}>
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            {document ? (
              <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
                <Link component={RouterLink} to={`/legal?lang=${lang}`} underline="hover" color="inherit">
                  {lang === 'de' ? 'Rechtliches' : 'Legal'}
                </Link>
                <Typography color="text.primary">{document.title[lang]}</Typography>
              </Breadcrumbs>
            ) : (
              <Box />
            )}
            <ButtonGroup size="small" variant="outlined">
              {supportedLocales.map((locale) => (
                <Button
                  key={locale}
                  onClick={() => handleLanguageChange(locale)}
                  variant={lang === locale ? 'contained' : 'outlined'}
                >
                  {languageLabels[locale]}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {DocumentComponent ? <DocumentComponent /> : <IndexComponent documents={legalDocuments} lang={lang} />}
          </Paper>
        </Container>
      </Box>
    </>
  );
}
